import {BaseCurrencyCode, DoubleString} from '@raison/types';
import BigNumber from 'bignumber.js';
import {PRIMARY_CURRENCY_DECIMALS} from '~/config';
import {MoneyFormat} from '~/library/money-kit';

export const createCurrencyService = (
    rates: Record<BaseCurrencyCode, Record<BaseCurrencyCode, number>>,
    primaryCurrency: BaseCurrencyCode
) => {
    type Amount = BigNumber | DoubleString | number;

    const convert = (amount: Amount, from: BaseCurrencyCode, to: BaseCurrencyCode) => {
        let coefficient = rates[to]?.[from];
        if (coefficient == null) {
            coefficient = 0;
        }
        return new BigNumber(amount).multipliedBy(coefficient);
    };

    const convertFrom = (currencyFrom: BaseCurrencyCode) => (currencyTo: BaseCurrencyCode) => (amount: Amount) =>
        convert(amount, currencyFrom, currencyTo);

    const convertTo = (currencyTo: BaseCurrencyCode) => (currencyFrom: BaseCurrencyCode) => (amount: Amount) =>
        convert(amount, currencyFrom, currencyTo);

    const convertToPrimary = (amount: Amount, currencyFrom: BaseCurrencyCode) =>
        convert(amount, currencyFrom, primaryCurrency);

    const formatAsPrimary = (amount: Amount, currencyCode: BaseCurrencyCode = primaryCurrency): string => {
        let converted: BigNumber;
        if (primaryCurrency === currencyCode) {
            converted = new BigNumber(amount);
        } else {
            converted = convert(amount, currencyCode, primaryCurrency);
        }
        const formatter = MoneyFormat.from({
            code: primaryCurrency,
            decimals: PRIMARY_CURRENCY_DECIMALS,
        });
        return formatter.localeFormat(converted.toNumber());
    };

    const checkRateToPrimary = (currencyCode: BaseCurrencyCode) => {
        return !!rates[currencyCode]?.[primaryCurrency];
    };

    return {
        checkRateToPrimary,
        convertToPrimary,
        formatAsPrimary,
        convertFrom,
        convertTo,
        convert,
    };
};

export type CurrencyService = ReturnType<typeof createCurrencyService>;
